import React, { useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const COLORS = ['#2e7d32', '#ffd700', '#ff6b35', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7', '#dfe6e9'];

function ConfettiCanvas({ active }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!active || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const particleCount = 80;
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 8 + 4,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        speedX: (Math.random() - 0.5) * 4,
        speedY: (Math.random() - 0.5) * 4 + 2,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 10,
      });
    }

    let animationId;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.speedX;
        p.y += p.speedY;
        p.rotation += p.rotationSpeed;
        p.speedY += 0.1;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx.restore();
      });
      animationId = requestAnimationFrame(animate);
    };
    animate();
    const stop = () => cancelAnimationFrame(animationId);
    const t = setTimeout(stop, 4000);
    return () => {
      clearTimeout(t);
      stop();
    };
  }, [active]);

  if (!active) return null;
  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9998,
      }}
    />
  );
}

export default function WinnerCongratulationsModal({ open, onClose, electionName, positionsWon, onSeen }) {
  const handleContinue = async () => {
    await onSeen();
    onClose();
  };

  return (
    <>
      <ConfettiCanvas active={open} />
      <Dialog
        open={open}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
        fullScreen
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: 'background.default',
            background: 'linear-gradient(135deg, rgba(46, 125, 50, 0.06) 0%, rgba(255, 215, 0, 0.04) 100%)',
          },
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 6 }}>
          <EmojiEventsIcon sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Congratulations!
          </Typography>
          <Typography variant="h6" color="primary.main">
            You Won!
          </Typography>
          {electionName && (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {electionName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
          {positionsWon && positionsWon.length > 0 && (
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Position(s) won:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                {positionsWon.map((pos) => (
                  <Typography
                    key={pos}
                    variant="h6"
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                    }}
                  >
                    {pos}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 400 }}>
            Thank you for your dedication. The members have spoken, and we are excited to have you serve in this capacity.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 6 }}>
          <Button variant="contained" size="large" onClick={handleContinue} color="primary">
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
